import { useCallback, useEffect, useRef, useState } from 'react';

export const useSlideNavigation = (slideCount: number, autoplay: boolean, interval: number) => {
  const hasMultipleSlides = slideCount > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Auto-slide logic
  useEffect(() => {
    if (!autoplay || !hasMultipleSlides || isPaused) {
      return;
    }

    timerRef.current = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slideCount);
    }, interval);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [autoplay, hasMultipleSlides, isPaused, activeIndex, interval, slideCount]);

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const goToPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  return {
    activeIndex,
    hasMultipleSlides,
    isPaused,
    setIsPaused,
    goToSlide,
    goToPrev,
    goToNext,
  };
};
